import { classifyStartRefusal } from './gateTruthPresentation';

/**
 * FIELD EXECUTION — presentation only.
 *
 * The convention this replaces is the daily report. A worker should not have
 * to write one: they record what happened, and the reporting is composed from
 * those records later. The field is a sensor network, not a reporting layer.
 *
 * TWO RULES THAT GOVERN EVERY SCREEN BUILT ON THIS
 *
 * 1. A COMMAND IS NOT AN OBSERVATION. START_WORK is a governed transition —
 *    confirmed, capability-gated, and it MAY REFUSE. ACTIVITY_STARTED is a
 *    recorded fact that cannot be refused storage. The contract says the two
 *    "are different acts by design; one does not imply the other", so they
 *    must never share a button, a colour, or a sentence.
 *
 * 2. A REFUSED PROJECTION NEVER ERASES THE OBSERVATION. Storage is never
 *    refused on readiness, status, gates or authority state. When the machine
 *    declines to project a stored fact onto canonical state, BOTH stand: the
 *    field said one thing, the model says another, and that contradiction is
 *    the product, not an error to clear away.
 *
 * 3. THE FIELD DATE IS THE HUMAN'S TO STATE. Every projection calls
 *    `_parse_payload_date` for its own date key — `startedOn`, `finishedOn`,
 *    `logDate`, `deliveredOn`, `receivedOn`, `verifiedOn` — and refuses
 *    without it, because a field date derived by truncating a submission
 *    timestamp says the crew started when the phone had signal.
 *
 *    Those keys used to sit in a `dates` list that no form read and no
 *    validator checked, while `requires` held only the non-date fields. The
 *    result was the exact failure this module's own comment warns about:
 *    tap "Work started", tap Record, receive
 *    `400 startedOn is required` for a field nobody had asked for. It shipped
 *    green because the browser fixture answers 200 to any body.
 *    `requiredFields()` is now the union, and it is what the form asks for.
 */

export const FIELD_CONTRACT = 'execution-observation-1';

/**
 * The closed observation vocabulary, CHECK-enforced by the database.
 *
 * `requires` lists the payload fields the backend refuses without. They are
 * enumerated here so a form can ask for them BEFORE the round trip — a field
 * worker who gets a 400 for a field nobody asked them for stops trusting the
 * app.
 */
export const OBSERVATION_TYPES = {
  ACTIVITY_STARTED: {
    label: 'Work started',
    target: 'instance',
    prompt: 'When did the crew start?',
    requires: [],
    dates: ['startedOn'],
  },
  ACTIVITY_PROGRESS_OBSERVED: {
    label: 'Progress',
    target: 'instance',
    prompt: 'How far along is it?',
    requires: ['percentComplete'],
    dates: [],
    /** Backwards movement is a fact, not an error — the contract is explicit. */
    note: 'Progress can go down. If it does, record what you see.',
  },
  ACTIVITY_COMPLETED: {
    label: 'Work complete',
    target: 'instance',
    prompt: 'When did it finish?',
    requires: [],
    dates: ['finishedOn'],
    note: 'Recording completion does not mark the work verified. Verification is a separate act.',
  },
  MANPOWER_OBSERVED: {
    label: 'Manpower',
    target: 'instance',
    prompt: 'How many were on it, and on what day?',
    requires: ['actualManpower'],
    dates: ['logDate'],
  },
  MATERIAL_DELIVERED: {
    label: 'Material delivered',
    target: 'material',
    prompt: 'The goods arrived.',
    requires: [],
    dates: ['deliveredOn'],
    optional: ['note'],
    note: 'Recording a delivery does not mark it received or verified. Those are separate acts with stronger evidence behind them.',
  },
  MATERIAL_RECEIVED: {
    label: 'Material received',
    target: 'material',
    prompt: 'An approved receiving record exists.',
    /** Authority-2 evidence. The backend refuses without both. */
    requires: ['receivingReference', 'approvedBy'],
    dates: ['receivedOn'],
    /**
     * The quantity is OPTIONAL and its absence is a stated answer: the
     * backend returns "The receiving record names no quantity; delivered_qty
     * was left as it stands." Leaving it blank must never become zero.
     */
    optional: ['quantityReceived', 'note'],
  },
  MATERIAL_VERIFIED: {
    label: 'Material verified',
    target: 'material',
    prompt: 'Someone stood next to it.',
    requires: ['quantitySufficient'],
    dates: ['verifiedOn'],
    optional: ['verifiedQuantity', 'evidence', 'note'],
    note: 'Sworn in means verified AND sufficient. Saying the quantity is short is a real answer.',
  },
  CONSTRAINT_OBSERVED: {
    label: 'Constraint seen',
    target: 'project',
    prompt: 'What is in the way?',
    requires: [],
    dates: [],
    note: 'This is recorded, not enforced. It gates nothing on its own.',
  },
  CONSTRAINT_CLEARED: {
    label: 'Constraint cleared',
    target: 'project',
    prompt: 'What was cleared?',
    requires: [],
    dates: [],
  },
};

/**
 * WHAT EACH PAYLOAD FIELD IS CALLED ON A JOB SITE.
 *
 * The form used to print the raw payload key as its own label, so a
 * superintendent standing at a gate was asked for `receivingReference` and
 * told "OMNΛS needs receivingReference and approvedBy". These are the same
 * fields in the words the trade already uses. The KEY is still what goes on
 * the wire — nothing here renames anything the backend reads.
 */
const PAYLOAD_FIELDS = {
  startedOn: {
    label: 'Date work started',
    kind: 'date',
    help: 'The day the crew actually started, not today.',
  },
  finishedOn: {
    label: 'Date work finished',
    kind: 'date',
    help: 'The day it was actually finished.',
  },
  logDate: {
    label: 'Day being logged',
    kind: 'date',
    help: 'The day these numbers are for.',
  },
  deliveredOn: {
    label: 'Date it arrived',
    kind: 'date',
    help: 'The day the material reached site.',
  },
  receivedOn: { label: 'Date on the receiving record', kind: 'date' },
  verifiedOn: { label: 'Date you looked at it', kind: 'date' },

  percentComplete: { label: 'Percent complete', kind: 'number' },
  actualManpower: { label: 'People on it', kind: 'number' },

  receivingReference: {
    label: 'Receiving record number',
    kind: 'text',
    help: 'The packing slip, delivery ticket or receipt number.',
  },
  approvedBy: {
    label: 'Who approved the receipt',
    kind: 'text',
    help: 'The person who signed it off, by name.',
  },
  quantityReceived: {
    label: 'Quantity on the record',
    kind: 'number',
    optionalHelp:
      'Leave blank if the record does not state one. Blank stays blank — it never becomes zero.',
  },
  quantitySufficient: {
    label: 'Was the quantity sufficient?',
    kind: 'boolean',
  },
  verifiedQuantity: {
    label: 'Quantity you counted',
    kind: 'number',
    optionalHelp:
      'Leave blank if you did not count it. Blank stays blank — it never becomes zero.',
  },
  evidence: {
    label: 'What you saw',
    kind: 'text',
    optionalHelp:
      'Written down with the material record. OMNΛS stores no photographs against an observation — there is no contract for that yet.',
  },
  note: {
    label: 'Note',
    kind: 'text',
    optionalHelp:
      'Stored word for word with your record. OMNΛS does not read it or act on it.',
  },
};

/** The field's own words, or its key if this screen has none for it. */
export const payloadFieldDisplay = (field) =>
  PAYLOAD_FIELDS[field] || { label: field, kind: 'text' };

/**
 * EVERYTHING THE BACKEND REFUSES WITHOUT — dates included.
 *
 * The date comes first because it is the question a person can answer while
 * still looking at the thing: when did this happen?
 */
export const requiredFields = (observationType) => {
  const spec = OBSERVATION_TYPES[observationType];
  if (!spec) return [];
  return [...(spec.dates || []), ...(spec.requires || [])];
};

/** The fields the backend reads if given, and states the absence of if not. */
export const optionalFields = (observationType) =>
  OBSERVATION_TYPES[observationType]?.optional || [];

/** "the date it arrived" — never "deliveredOn". */
export const describeMissing = (fields) => {
  const names = (fields || []).map((f) =>
    payloadFieldDisplay(f).label.toLowerCase(),
  );
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

export const OBSERVATION_TYPE_KEYS = Object.keys(OBSERVATION_TYPES);

/** Which observation types apply to a given target. */
export const observationTypesFor = (target) =>
  OBSERVATION_TYPE_KEYS.filter(
    (key) => OBSERVATION_TYPES[key].target === target,
  );

export const observationTypeDisplay = (type) => OBSERVATION_TYPES[type] || null;

/**
 * The one governed command.
 *
 * Kept OUT of `OBSERVATION_TYPES` on purpose: putting it in the same list
 * would invite a screen to render it as a tenth observation, and a command
 * that can refuse is not a fact that cannot.
 */
export const START_WORK_COMMAND = {
  key: 'START_WORK',
  label: 'Start work',
  kind: 'COMMAND',
  meaning:
    'A request to move the work into progress. OMNΛS checks the gates and may refuse, naming its reasons.',
  requiresConfirmation: true,
};

/** Is this a recorded fact or a requested transition? */
export const actKind = (key) =>
  key === START_WORK_COMMAND.key ? 'COMMAND' : 'OBSERVATION';

/**
 * What happened to the stored fact when the machine tried to apply it.
 *
 * REFUSED is not a failure to record. The observation is stored either way;
 * this says whether canonical state moved.
 */
export const PROJECTION_STATES = ['PROJECTED', 'REFUSED', 'NOT_APPLICABLE'];

const PROJECTION_DISPLAY = {
  PROJECTED: {
    label: 'Recorded, and the project moved',
    tone: 'good',
    meaning: 'OMNΛS applied this to canonical state.',
  },
  REFUSED: {
    label: 'Recorded — the project did not move',
    tone: 'attention',
    meaning:
      'What you recorded stands. OMNΛS declined to apply it and said why. Both positions are kept; neither erases the other.',
  },
  NOT_APPLICABLE: {
    label: 'Recorded',
    tone: 'neutral',
    meaning:
      'Nothing in canonical state corresponds to this yet, so there was nothing to move.',
  },
};

export const projectionDisplay = (state) =>
  PROJECTION_DISPLAY[state] || PROJECTION_DISPLAY.NOT_APPLICABLE;

/**
 * One stored observation.
 *
 * A refusal is surfaced as its own standing fact rather than folded into an
 * error state, because the contradiction between field and model is
 * reconstructible and worth reading.
 */
export const presentObservation = (row) => {
  const r = row || {};
  const state = PROJECTION_STATES.includes(r.projection?.state)
    ? r.projection.state
    : 'NOT_APPLICABLE';
  const type = OBSERVATION_TYPES[r.observationType] || null;
  return {
    id: r.id || null,
    observationType: r.observationType || null,
    typeLabel: type ? type.label : r.observationType || 'Observation',
    targetEntity: r.targetEntity || null,
    targetId: r.targetId || null,
    constraintKey: r.constraintKey || null,
    observedAt: r.observedAt || null,
    recordedAt: r.recordedAt || null,
    payload: r.payload && typeof r.payload === 'object' ? r.payload : {},
    /** CHECK-locked HUMAN_ASSERTED. No AI physical fact can enter the table. */
    provenance: r.provenance || 'HUMAN_ASSERTED',
    actorId: r.actorId || null,
    projection: {
      state,
      display: projectionDisplay(state),
      /** The machine's own words at recording time. Unreconstructible later. */
      detail: r.projection?.detail || null,
      refused: state === 'REFUSED',
    },
    supersedesId: r.supersedesId || null,
    /** The record stands even when the projection did not. Always true. */
    stored: true,
  };
};

/**
 * An idempotency key for one submission.
 *
 * The backend requires it (max 120 chars) and replays it: the same key
 * against the same target returns the stored row rather than duplicating, and
 * against a DIFFERENT target is a 409. So it must be stable for one attempt
 * and unique across attempts — generated once when the form opens, not on
 * every render.
 */
export const buildIdempotencyKey = (targetId, observationType, nonce) =>
  `${observationType}:${targetId}:${nonce}`.slice(0, 120);

/**
 * Which required fields a payload is still missing.
 *
 * Checked before sending so the worker is asked once, in the form, rather
 * than being handed a 400 for a field nobody prompted for.
 */
export const missingRequiredFields = (observationType, payload) => {
  const type = OBSERVATION_TYPES[observationType];
  if (!type) return [];
  const p = payload || {};
  // The union, not `requires` alone. A date key the form never asked for is
  // a 400 the worker cannot act on. See rule 3 in the module header.
  return requiredFields(observationType).filter((field) => {
    const value = p[field];
    // A real boolean false IS an answer — a short quantity is a legitimate
    // negative attestation, not an empty field.
    if (typeof value === 'boolean') return false;
    if (typeof value === 'number') return Number.isNaN(value);
    return value === undefined || value === null || value === '';
  });
};

/**
 * Build the request body.
 *
 * `quantitySufficient` is coerced to a REAL boolean because the backend
 * refuses a string: a coerced "false" would otherwise read as an attestation
 * that the quantity was fine.
 */
export const buildObservationRequest = ({
  observationType,
  observedAt,
  payload,
  idempotencyKey,
  constraintKey,
  supersedesId,
}) => {
  const body = {
    observationType,
    observedAt,
    payload: { ...(payload || {}) },
    idempotencyKey,
  };
  if (typeof body.payload.quantitySufficient === 'string') {
    body.payload.quantitySufficient =
      body.payload.quantitySufficient === 'true';
  }
  if (typeof body.payload.percentComplete === 'string') {
    body.payload.percentComplete = Number(body.payload.percentComplete);
  }
  if (typeof body.payload.actualManpower === 'string') {
    body.payload.actualManpower = Number(body.payload.actualManpower);
  }
  /*
    The OPTIONAL quantities. A blank field is REMOVED from the payload rather
    than sent as '' or 0. The backend distinguishes "no quantity was stated"
    — which it reports back as an unknown, leaving the stored quantity alone
    — from a counted zero. Sending 0 for a blank box would swear to a count
    nobody made.
  */
  for (const key of ['quantityReceived', 'verifiedQuantity']) {
    const value = body.payload[key];
    if (value === '' || value === null || value === undefined) {
      delete body.payload[key];
    } else if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isNaN(parsed)) delete body.payload[key];
      else body.payload[key] = parsed;
    }
  }
  for (const key of ['note', 'evidence', 'receivingReference', 'approvedBy']) {
    if (
      typeof body.payload[key] === 'string' &&
      body.payload[key].trim() === ''
    ) {
      delete body.payload[key];
    }
  }
  if (constraintKey) body.constraintKey = constraintKey;
  if (supersedesId) body.supersedesId = supersedesId;
  return body;
};

/**
 * Why a submission was refused, kept as distinct answers.
 *
 * A START_WORK refusal is a governed outcome carrying the gates' named
 * reasons — worth reading, not an error to dismiss.
 */
/**
 * A governed START_WORK refusal and an idempotency clash are BOTH 409, and
 * they are nothing alike. Before `what-can-start-2` the generic branch
 * caught both, so a runtime refusal read as "that key belongs to a different
 * record" — which is not what happened and gives a person nothing to act on.
 * The coded branch runs first, and the refusal keeps its own class.
 */
export const presentFieldRefusal = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const detail = typeof data === 'object' && data ? data.detail : null;
  const code =
    (typeof data === 'object' && data && data.code) ||
    (typeof detail === 'object' && detail && detail.code) ||
    null;
  const message =
    (typeof detail === 'string' && detail) ||
    detail?.message ||
    data?.message ||
    null;

  if (status === 428 || code === 'confirmation_required') {
    return {
      kind: 'CONFIRMATION_REQUIRED',
      title: 'Confirm before starting',
      detail:
        'Starting work is a governed act. OMNΛS asks you to confirm rather than assuming.',
      storedAnyway: false,
    };
  }
  if (status === 400) {
    return {
      kind: 'SHAPE',
      title: 'Something in that record is not usable',
      detail:
        message || 'A field is missing or outside the range OMNΛS accepts.',
      storedAnyway: false,
    };
  }
  if (status === 409 && code === 'start_refused') {
    const refusal = classifyStartRefusal(message);
    return {
      kind: 'START_REFUSED',
      refusalClass: refusal.key,
      title: refusal.headline,
      detail: refusal.meaning,
      // The runtime's own sentence, kept whole beside our classification.
      serverMessage: refusal.message || null,
      caveat: refusal.caveat,
      tone: refusal.tone,
      storedAnyway: false,
    };
  }
  if (status === 409) {
    return {
      kind: 'IDEMPOTENCY_CONFLICT',
      title: 'That key belongs to a different record',
      detail:
        'The same submission key was already used for another target. OMNΛS will not quietly attach it to this one.',
      storedAnyway: false,
    };
  }
  if (status === 503 || code === 'not_enabled') {
    return {
      kind: 'NOT_ENABLED',
      title: 'Field recording is switched off',
      detail: 'A platform-wide setting. Nothing you did caused it.',
      storedAnyway: false,
    };
  }
  if (status === 403 && code === 'tenant_not_entitled') {
    return {
      kind: 'NOT_ENTITLED',
      title: 'This tenant is not entitled to field recording',
      detail: 'An OMNΛS operator grants this.',
      storedAnyway: false,
    };
  }
  if (status === 403 && code === 'capability_required') {
    return {
      kind: 'CAPABILITY_REQUIRED',
      title: 'You do not have the capability to record this',
      detail:
        'Recording what you see and moving the work forward are separate permissions, deliberately.',
      storedAnyway: false,
    };
  }
  if (status === 403) {
    return {
      kind: 'ROLE_REFUSED',
      title: 'Your role cannot record this',
      detail: 'A superintendent on this project can.',
      storedAnyway: false,
    };
  }
  if (status === 404) {
    return {
      kind: 'NOT_FOUND',
      title: 'That work front was not found',
      detail: 'It may belong to another tenant, or it may not exist.',
      storedAnyway: false,
    };
  }
  return {
    kind: 'UNAVAILABLE',
    title: 'That record did not reach OMNΛS',
    detail: 'Nothing was stored. Try again when you have signal.',
    storedAnyway: false,
  };
};
